/**
 * The root: a saved session resumes, otherwise sign in. Honest states:
 * "Connecting" until the gateway's READY, a calm offline screen when the
 * server can't be reached.
 */
import { useEffect, useState } from 'react';
import { useT } from './i18n';
import { AuthScreen, ResetPassword } from './auth/Auth';
import { forgetLinkParam, takeLinkParam } from './auth/links';
import { restoreSession, useSession } from './store/session';
import { useData } from './store/data';
import { FrameTitleBar, Shell } from './shell/Shell';
import { RecoveryCodes } from './settings/Security';
import { Btn } from './ui/kit';
import { Dialog, OverlayHost } from './ui/overlay';
import { EmojiSheetHost } from './hub/emoji';
import { ForwardHost } from './hub/Forward';
import { signOut } from './store/session';
import markUrl from './assets/mark.svg';
import { loadConfig, useConfig, useMaintenancePhase } from './store/config';
import { ConfigBanners, MaintenanceScreen } from './shell/ConfigNotices';
import { takeInviteLink } from './shell/InviteLink';
import { AddEmailGate, verifyLinkOnLoad } from './account/Email';
import { AppOffScreen, useAppOff, useUndoEmailLink } from './shell/AccountGates';
import { UpdateRequiredScreen, useUpdateRequired } from './shell/Update';

function Splash({ text, action }: { text: string; action?: { label: string; run(): void } }) {
  return (
    <div className="ns-splash" role="status" aria-live="polite">
      <div className="nx-sky" aria-hidden="true">
        <i className="aur aur-hub" />
        <i className="grain" />
      </div>
      <img src={markUrl} alt="" width={56} height={56} className="ns-splash-mark" />
      <p>
        <i className="nx-spin" /> {text}
      </p>
      {action ? (
        <Btn kind="ghost" size="sm" onClick={action.run}>
          {action.label}
        </Btn>
      ) : null}
    </div>
  );
}

function CodesAfterSignup() {
  const { t } = useT();
  const codes = useSession((s) => s.recoveryCodes);
  const set = useSession((s) => s.set);
  if (!codes) return null;
  return (
    <Dialog
      title={t('auth.codes.title')}
      icon="shield"
      tone="mode"
      dismissable={false}
      onClose={() => {}}
    >
      <RecoveryCodes codes={codes} mustConfirm onDone={() => set({ recoveryCodes: null })} />
    </Dialog>
  );
}

/**
 * A reset link opened while signed in: the same screen in a dialog. Done, it
 * signs out here too (the server already ended every session); Back only
 * closes the dialog.
 */
function ResetWhileSignedIn() {
  const { t } = useT();
  const [token] = useState(() => takeLinkParam('reset_password'));
  const [open, setOpen] = useState(!!token);
  if (!open || !token) return null;
  const close = () => (forgetLinkParam('reset_password'), setOpen(false));
  return (
    <Dialog label={t('auth.reset.title')} cls="ns-reset-dlg" onClose={close} width={460}>
      <ResetPassword
        token={token}
        go={(s) => (s === 'signin' ? void signOut(true) : close())}
        onBack={close}
      />
    </Dialog>
  );
}

/**
 * A staff member choosing "Staff sign-in" on the maintenance screen: the
 * sign-in screen, then (signed in) a check of their own config, which says
 * whether the server lets them through.
 */
type StaffEntry = 'no' | 'signing_in' | 'checked';

export function App() {
  const { t } = useT();
  const status = useSession((s) => s.status);
  const conn = useData((d) => d.connection);
  const maintenance = useMaintenancePhase();
  // the console turned this app (web or desktop) off
  const appOff = useAppOff();
  // this desktop app is older than the oldest version allowed (Console v2 §3.7)
  const updateMin = useUpdateRequired();
  // the old address's "This wasn't me" link, being used
  const undoing = useUndoEmailLink();
  // staff keep the app during maintenance (the server lets them through)
  const exempt = useConfig((s) => !!s.config?.maintenance.exempt);
  const [staffEntry, setStaffEntry] = useState<StaffEntry>('no');
  // an invite link (/invite/CODE): read once, used after sign-in (InviteLinkHost)
  useState(() => takeInviteLink());
  useEffect(() => {
    void loadConfig();
    void restoreSession();
    // a verification link from an email (works signed in or out)
    void verifyLinkOnLoad();
  }, []);
  useEffect(() => {
    if (staffEntry === 'signing_in' && status === 'ready')
      void loadConfig({ force: true }).then(() => setStaffEntry('checked'));
  }, [staffEntry, status]);
  const signedIn = status === 'connecting' || status === 'ready' || status === 'offline';
  const blocked = maintenance === 'active' && !exempt;
  let body;
  let inApp = false;
  // the operator's maintenance mode: a calm screen instead of the app
  if (blocked && staffEntry !== 'signing_in')
    body = (
      <MaintenanceScreen
        signedIn={signedIn}
        onStaffSignIn={status === 'signed_out' ? () => setStaffEntry('signing_in') : undefined}
      />
    );
  else if (blocked && status !== 'signed_out' && status !== 'offline')
    body = <Splash text={t('ns.conn.connecting')} />;
  else if (updateMin) body = <UpdateRequiredScreen min={updateMin} signedIn={signedIn} />;
  else if (appOff) body = <AppOffScreen kind={appOff} />;
  else if (undoing) body = <Splash text={t('email.undo.working')} />;
  else if (status === 'checking') body = <Splash text={t('ns.loading')} />;
  else if (status === 'signed_out') body = <AuthScreen />;
  else if (status === 'offline')
    body = <AuthScreen offline onRetry={() => void restoreSession()} />;
  else if (status === 'connecting')
    body = (
      <Splash
        text={conn === 'reconnecting' ? t('ns.conn.reconnecting') : t('ns.conn.connecting')}
        action={
          conn === 'reconnecting'
            ? { label: t('ns.signout'), run: () => void signOut() }
            : undefined
        }
      />
    );
  else {
    inApp = true;
    body = (
      <>
        {/* the banners have their own row in the app frame (Shell) */}
        <Shell />
        <CodesAfterSignup />
        {/* an account made before email sign-in: "Add your email", not skippable */}
        <AddEmailGate />
        {/* staff reset the username: Shell's StaffNotices asks for a new one */}
        <ResetWhileSignedIn />
        {/* a server invite link opened while signed in: InviteLinkHost in Shell */}
      </>
    );
  }
  return (
    <>
      {inApp ? (
        body
      ) : (
        // outside the app (sign-in, connecting): the banners in a row above, never over it;
        // on the desktop the window's title bar comes first (QA DESK-1)
        <div className="ns-frame">
          <FrameTitleBar />
          {!blocked ? <ConfigBanners /> : null}
          <div className="ns-frame-body">{body}</div>
        </div>
      )}
      <OverlayHost />
      {/* the emoji picker as a sheet on a phone (§12.7 A5) */}
      <EmojiSheetHost />
      {/* a message's Forward picker (2026-10-29) */}
      <ForwardHost />
    </>
  );
}
